import { Button, Field } from "@deligoo/ui"
import { unwrapResult } from "@reduxjs/toolkit"
import dayjs from "dayjs"
import { CSSProperties, memo, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { toast } from "react-toastify"
import { AutoSizer } from "react-virtualized/dist/commonjs/AutoSizer"
import { areEqual, FixedSizeList } from "react-window"

import { useAppDispatch, useAppSelector } from "@/App"
import { Card } from "@/components/Card"
import { Clipboard } from "@/components/Clipboard"
import { ConfirmModal } from "@/components/ConfirmModal"
import { Table } from "@/components/Table"
import {
  assignOrder,
  selectOrdersWithoutPaymentsById,
  selectOrdersWithoutPaymentsIds,
  selectOrdersWithoutPaymentsStatus,
} from "@/store/payments"
import { selectCurrentTeamId } from "@/store/teams"
import { Order, OrderWithoutPayment } from "@/types"
import { formatPrice } from "@/utils"

import cls from "../PaymentsView.module.scss"

const TABLE_HEADERS = ["order_uid", "created_at", "amount", "driver"] as const

const ROW_HEIGHT_PX = 75 as const

export const OrdersWithoutPayments = () => {
  const ordersIds = useAppSelector(selectOrdersWithoutPaymentsIds)
  const ordersFetchStatus = useAppSelector(selectOrdersWithoutPaymentsStatus)

  const tBodyStyle: CSSProperties = useMemo(() => {
    const minHeightValue = ordersIds.length > 4 ? ROW_HEIGHT_PX * 4 : ROW_HEIGHT_PX * ordersIds.length

    return {
      minHeight: `${minHeightValue}px`,
    }
  }, [ordersIds])

  const { t } = useTranslation("payments")

  return (
    <Card box className="mt-6">
      <Card.Header className={cls.settlementsHeader}>Zamówienia bez numeru transakcji</Card.Header>

      <Table
        minWidth="1200px"
        columns="repeat(5, minmax(200px, 1fr)"
        loading={ordersFetchStatus === "loading"}
        error={ordersFetchStatus === "error"}
        empty={!ordersIds || ordersIds.length === 0}
        virtualized
      >
        <Table.Thead>
          <Table.Tr>
            {TABLE_HEADERS.map((header) => (
              <Table.Th key={header}>{t(`orders_without_payments_list_headers.${header}`)}</Table.Th>
            ))}
            <Table.Th />
          </Table.Tr>
        </Table.Thead>

        <Table.Tbody style={tBodyStyle}>
          <AutoSizer>
            {({ height, width }) => (
              <FixedSizeList
                height={height}
                width={width}
                overscanCount={4}
                itemData={ordersIds}
                itemCount={ordersIds.length}
                itemKey={(index, data) => data[index]}
                itemSize={ROW_HEIGHT_PX}
              >
                {OrdersWithoutPaymentsRow}
              </FixedSizeList>
            )}
          </AutoSizer>
        </Table.Tbody>
      </Table>
    </Card>
  )
}

type OrdersWithoutPaymentsRowProps = {
  data: Array<OrderWithoutPayment["id"]>
  index: number
  style: CSSProperties
}

const OrdersWithoutPaymentsRow = memo(({ data, index, style }: OrdersWithoutPaymentsRowProps) => {
  const orderId = data[index]

  return <OrdersWithoutPaymentsItem orderId={orderId} style={style} key={orderId} />
}, areEqual)

const OrdersWithoutPaymentsItem = ({
  orderId,
  style,
}: {
  orderId: OrderWithoutPayment["id"]
  style: CSSProperties
}) => {
  const order = useAppSelector(selectOrdersWithoutPaymentsById(orderId))

  const [updateOrderPaymentId, setUpateOrderPaymentId] = useState<Order["id"] | null>(null)

  if (!order || !order?.order_uid) return null

  return (
    <>
      <Table.Tr style={style}>
        <Table.Td>
          <Clipboard>{order.order_uid}</Clipboard>
        </Table.Td>

        <Table.Td>{dayjs(order.created_at).format("DD.MM.YYYY HH:mm")}</Table.Td>

        <Table.Td>{formatPrice(order.full_price_subunit, { subunit: true })} zł</Table.Td>

        <Table.Td>
          {order.driver_email ? <Clipboard className="truncate">{order.driver_email}</Clipboard> : "-"}
        </Table.Td>

        <Table.Td className="text-right">
          <Button size="small" onClick={() => setUpateOrderPaymentId(order.id)}>
            Powiąż płatność
          </Button>
        </Table.Td>
      </Table.Tr>

      {updateOrderPaymentId && (
        <UpdateOrderPayment orderUid={order.order_uid} handleClose={() => setUpateOrderPaymentId(null)} />
      )}
    </>
  )
}

const TOAST_OPTIONS = {
  toastId: "updatePayment",
  updateId: "updatePayment",
}

export const UpdateOrderPayment = ({ orderUid, handleClose }: { orderUid: Order["uid"]; handleClose: () => void }) => {
  const currentTeamId = useAppSelector(selectCurrentTeamId)

  const [transactionCode, setTransactionCode] = useState("")
  const [isLoading, setIsLoading] = useState(false)

  const dispatch = useAppDispatch()

  const { t } = useTranslation("payments")

  async function handleUpdateOrderPayment() {
    if (!currentTeamId) return

    if (!orderUid || !transactionCode) {
      return toast.error(t("payment_update_empty_params"), TOAST_OPTIONS)
    }

    setIsLoading(true)

    const response = await dispatch(assignOrder({ transactionCode, body: { order_uid: orderUid } })).then(unwrapResult)

    if (response.ok) {
      toast.success(t("payment_update_success"), TOAST_OPTIONS)
    }

    setIsLoading(false)
    handleClose()
  }

  return (
    <ConfirmModal
      title={`Powiąż płatność z zamówieniem #${orderUid}`}
      handleConfirm={handleUpdateOrderPayment}
      handleClose={handleClose}
      isLoading={isLoading}
      contentCenter
    >
      <Field
        label="Kod transakcji"
        name="transaction_code"
        inputProps={{
          onChange: (e) => setTransactionCode(e.currentTarget.value),
          defaultValue: transactionCode,
          autoFocus: true,
        }}
      />
    </ConfirmModal>
  )
}
